import { Tooltip } from '@odo/shared-ui';

const RING_SIZE = 104;
const STROKE_WIDTH = 9;
const RADIUS = (RING_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const EMPTY_STROKE = '#f3f4f6';

interface StatRingProps {
    pct: number | null;
    color: string;
    label: string;
    sublabel: string;
    title?: string;
    emptyLabel?: string;
}

/**
 * Aligner-progress ring, ported from files-viewer's
 * `patient-files/overview/StatRing.tsx` so the doctor dashboard's Treatment
 * progress card matches that design exactly. Not shared via `@odo/shared-ui`
 * because the source component is private to files-viewer's overview
 * feature — this is a faithful copy, not a re-export.
 */
function StatRing({ pct, color, label, sublabel, title, emptyLabel = 'No data' }: StatRingProps) {
    const hasValue = pct != null;
    const offset = hasValue ? CIRCUMFERENCE - (pct / 100) * CIRCUMFERENCE : CIRCUMFERENCE;

    const body = (
        <div className="flex flex-col items-center gap-2 w-full min-w-[110px]">
            <div className="relative flex-shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
                <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90">
                    <circle
                        cx={RING_SIZE / 2}
                        cy={RING_SIZE / 2}
                        r={RADIUS}
                        fill="none"
                        stroke={EMPTY_STROKE}
                        strokeWidth={STROKE_WIDTH}
                    />
                    {hasValue && (
                        <circle
                            cx={RING_SIZE / 2}
                            cy={RING_SIZE / 2}
                            r={RADIUS}
                            fill="none"
                            stroke={color}
                            strokeWidth={STROKE_WIDTH}
                            strokeLinecap="round"
                            strokeDasharray={CIRCUMFERENCE}
                            strokeDashoffset={offset}
                            className="transition-all duration-700"
                        />
                    )}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span
                        className="text-base font-semibold"
                        style={{ color: hasValue ? color : '#9ca3af' }}
                    >
                        {hasValue ? `${pct}%` : '--'}
                    </span>
                </div>
            </div>
            <div className="flex flex-col items-center text-center min-w-0 max-w-full">
                <span className="text-sm font-medium text-gray-800 truncate max-w-full">
                    {hasValue ? label : emptyLabel}
                </span>
                <span className="text-xs text-gray-500 truncate max-w-full">{sublabel}</span>
            </div>
        </div>
    );

    return title ? <Tooltip content={title}>{body}</Tooltip> : body;
}

export { StatRing };
